import { cn } from "@/lib/utils";
import { formatDistanceToNow } from "date-fns";
import {
  Calendar,
  GitBranch,
  GitMerge,
  GitPullRequest,
  User,
} from "lucide-react";
import { Badge } from "../../ui/badge";

import type { GithubPullRequest } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardMetadata,
  BaseEntityCardMetadataItem,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export { GithubPullRequest as PullRequest };

type PullRequestCardProps = GithubPullRequest & BaseEntityCardProps;

export function PullRequestCardAvatar({
  merged,
  draft,
  state,
  className,
  style,
}: Partial<PullRequestCardProps>) {
  const Icon = merged ? GitMerge : GitPullRequest;
  const color = merged
    ? "text-plum"
    : draft
      ? "text-neutral-400"
      : state === "closed"
        ? "text-lc-blocked"
        : "text-lc-active";

  return (
    <BaseEntityCardIcon className={className} style={style}>
      <Icon className={`h-5 w-5 ${color}`} />
    </BaseEntityCardIcon>
  );
}

export function PullRequestCard({
  platform,
  number,
  title,
  body = "",
  state,
  draft,
  merged,
  author,
  head_ref,
  base_ref,
  updated_at,
  merged_at,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
  web_link,
}: PullRequestCardProps) {
  const formattedUpdatedDate = updated_at
    ? formatDistanceToNow(new Date(updated_at), { addSuffix: true })
    : "";

  const formattedMergedDate = merged_at
    ? formatDistanceToNow(new Date(merged_at), { addSuffix: true })
    : "";

  const descriptionPreview =
    body && body.length > 100 ? `${body.substring(0, 100)}...` : body;

  // Map state to color - subtle text-only styling
  const getStateColor = () => {
    if (merged) {
      return "text-plum dark:text-plum border-purple-600/20";
    } else if (state === "closed") {
      return "text-lc-blocked dark:text-lc-blocked border-red-600/20";
    } else if (draft) {
      return "text-muted-foreground";
    } else {
      return "text-lc-active dark:text-lc-active border-green-600/20";
    }
  };

  const getStateLabel = () => {
    if (merged) return "Merged";
    if (draft) return "Draft";
    if (state === "closed") return "Closed";
    return "Open";
  };

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <PullRequestCardAvatar merged={merged} draft={draft} state={state} />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle platform={platform}>
            #{number}: {title}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            {web_link && (
              <BaseEntityCardExternalLinkAction
                href={web_link}
                tooltip="Open pull request"
              />
            )}
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <BaseEntityCardDescription className="line-clamp-2 text-foreground">
              {descriptionPreview}
            </BaseEntityCardDescription>
          )}

          <BaseEntityCardTags>
            <Badge variant="outline" className={cn("text-xs", getStateColor())}>
              {getStateLabel()}
            </Badge>

            {author && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <User className="h-3 w-3" />
                <span>{author}</span>
              </div>
            )}

            {head_ref && base_ref && (
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <GitBranch className="h-3 w-3" />
                <span>
                  {head_ref} → {base_ref}
                </span>
              </div>
            )}
          </BaseEntityCardTags>

          {(formattedUpdatedDate || formattedMergedDate) && (
            <BaseEntityCardMetadata>
              <BaseEntityCardMetadataItem
                icon={<Calendar className="h-3 w-3" />}
              >
                {formattedMergedDate
                  ? `Merged ${formattedMergedDate}`
                  : `Updated ${formattedUpdatedDate}`}
              </BaseEntityCardMetadataItem>
            </BaseEntityCardMetadata>
          )}
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
